import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: 'https://www.vt.mptransfer.fr/wp-content/uploads/2025/10/827cb073-5c8f-4abe-8006-6d015b9bf9cf.png',
      title: 'VOYAGE ELITE',
      subtitle: 'Chauffeur Privé Premium vers les Alpes',
      alt: 'Chauffeur privé VTC premium Genève Lyon - Mercedes luxe transfert stations ski Alpes Courchevel Chamonix'
    },
    {
      image: 'https://www.vt.mptransfer.fr/wp-content/uploads/2025/09/vclass-snow.png',
      title: 'CONFORT & ÉLÉGANCE',
      subtitle: 'Mercedes Classe E & Van de Luxe',
      alt: 'Mercedes Classe V luxe neige - Transport privé premium stations ski Alpes - Voyage Elite VTC'
    },
    {
      image: 'https://www.vt.mptransfer.fr/wp-content/uploads/2025/10/image-vclass-neige-jour.png',
      title: 'SERVICE 24/7',
      subtitle: 'Disponible à tout moment pour vos transferts',
      alt: 'Service transfert aéroport 24/7 - Chauffeur privé disponible jour nuit - VTC Genève Lyon stations ski'
    }
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="relative min-h-screen flex items-center justify-center overflow-hidden" role="banner">
      {/* Carrousel d'images de fond */}
      <div className="absolute inset-0 z-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 1.2, ease: [0.43, 0.13, 0.23, 0.96] }}
            className="absolute inset-0"
            style={{
              transform: `translateY(${scrollY * 0.4}px)`,
              willChange: 'transform'
            }}
          >
            <img
              src={slides[currentSlide].image}
              alt={slides[currentSlide].alt}
              title={`${slides[currentSlide].title} - Voyage Elite Transport Privé Premium`}
              className="w-full h-full object-cover object-center"
              loading={currentSlide === 0 ? 'eager' : 'lazy'}
              fetchpriority={currentSlide === 0 ? 'high' : 'low'}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65"></div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Contenu */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 py-20">
        <div className="max-w-5xl mx-auto text-center">
          {/* Titre principal avec animation */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -25 }}
              transition={{ duration: 0.7, ease: [0.43, 0.13, 0.23, 0.96] }}
            >
              <h1 className="font-playfair text-5xl sm:text-6xl lg:text-8xl font-bold text-white mb-6 leading-tight tracking-wide drop-shadow-2xl">
                {slides[currentSlide].title}
              </h1>
              <p className="text-white/95 font-inter text-xl sm:text-2xl lg:text-3xl mb-12 leading-relaxed drop-shadow-lg">
                {slides[currentSlide].subtitle}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Boutons d'action proéminents */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-16"
          >
            <motion.a
              href="/reservation"
              whileHover={{ scale: 1.06, boxShadow: '0 20px 40px rgba(212, 175, 55, 0.5)' }}
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-voyage-gold via-amber-500 to-voyage-gold text-night-blue rounded-full font-inter font-bold text-lg hover:from-amber-500 hover:via-voyage-gold hover:to-amber-500 transition-all shadow-2xl whitespace-nowrap cursor-pointer group bg-[length:200%_100%] animate-gradient"
              aria-label="Réserver votre transfert privé VTC en ligne - Genève Lyon stations ski"
            >
              <i className="ri-calendar-check-line text-2xl" aria-hidden="true"></i>
              <strong>RÉSERVER EN LIGNE</strong>
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform" aria-hidden="true"></i>
            </motion.a>

            <motion.a
              href="/contact"
              whileHover={{ scale: 1.06, backgroundColor: 'rgba(212, 175, 55, 0.25)' }}
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-white/10 backdrop-blur-md text-white rounded-full font-inter font-bold text-lg hover:bg-white/20 transition-all border-3 border-white/40 whitespace-nowrap cursor-pointer group shadow-xl"
              aria-label="Contactez Voyage Elite pour réservation chauffeur privé"
            >
              <i className="ri-phone-line text-2xl" aria-hidden="true"></i>
              <strong>CONTACT</strong>
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-2 transition-transform" aria-hidden="true"></i>
            </motion.a>
          </motion.div>

          {/* Indicateurs de diaporama */}
          <div className="flex justify-center gap-3 mb-8" role="tablist" aria-label="Navigation du carrousel">
            {slides.map((slide, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`transition-all duration-300 cursor-pointer ${
                  currentSlide === index
                    ? 'w-12 h-3 bg-voyage-gold rounded-full shadow-lg'
                    : 'w-3 h-3 bg-white/40 rounded-full hover:bg-white/60'
                }`}
                role="tab"
                aria-selected={currentSlide === index}
                aria-label={`Diapositive ${index + 1} : ${slide.title}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Indicateur de scroll animé */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.button
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 cursor-pointer bg-transparent border-0"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
          aria-label="Faire défiler vers le contenu"
        >
          <span className="text-white/80 font-inter text-xs uppercase tracking-wider">Découvrir</span>
          <i className="ri-arrow-down-line text-white/80 text-2xl" aria-hidden="true"></i>
        </motion.button>
      </motion.div>
    </header>
  );
}
